import { currencyFormat } from "../formatCurrency";

function isDateBetween(dateToCheck, startDate, endDate) {
    return dateToCheck >= startDate && dateToCheck <= endDate;
}

function isTimeBetween(timeToCheck, startTime, endTime) {
    return timeToCheck >= startTime && timeToCheck <= endTime;
}

export function isPromoActive(promoData) {
    if (!promoData || !promoData.group_product_promo) return false;

    const group = promoData.group_product_promo;
    const dates = Array.isArray(group.product_promo_dates) ? group.product_promo_dates : [];
    const times = Array.isArray(group.product_promo_times) ? group.product_promo_times : [];

    const currentDate = new Date();
    const currentTime = currentDate.toLocaleTimeString('en-US', { hour12: false });
    const currentDateString = currentDate.toISOString().split('T')[0];

    const isValidDate = dates.some(dateRange =>
        isDateBetween(currentDateString, dateRange.date_start, dateRange.date_end)
    );

    const isValidTime = times.some(timeRange =>
        isTimeBetween(currentTime, timeRange.time_start, timeRange.time_end)
    );

    return isValidDate && isValidTime;
}

export function isPriceActive(priceData) {
    if (!priceData || !priceData.group_product_price) return false;

    const group = priceData.group_product_price;
    const dates = Array.isArray(group.product_price_dates) ? group.product_price_dates : [];
    const times = Array.isArray(group.product_price_times) ? group.product_price_times : [];

    const currentDate = new Date();
    const currentTime = currentDate.toLocaleTimeString('en-US', { hour12: false });
    const currentDateString = currentDate.toISOString().split('T')[0];

    const isValidDate = dates.some(dateRange =>
        isDateBetween(currentDateString, dateRange.date_start, dateRange.date_end)
    );

    const isValidTime = times.some(timeRange =>
        isTimeBetween(currentTime, timeRange.time_start, timeRange.time_end)
    );

    return isValidDate && isValidTime;
}

function calculateDiscountedPrice(price, discountPercent) {
    const discountAmount = Number(price) * (Number(discountPercent) / 100);
    return Number(price) - discountAmount;
}

function getProductPromos(item) {
    return Array.isArray(item?.product_promos) ? item.product_promos : [];
}

function getProductPrices(item) {
    return Array.isArray(item?.product_prices) ? item.product_prices : [];
}

function getProductCategoryPrices(item) {
    return Array.isArray(item?.product_category_prices) ? item.product_category_prices : [];
}

function getClearancePrice(item) {
    return Number(
        item?.price_clearance ??
        item?.clearance_price ??
        item?.edit_price ??
        item?.price ??
        0
    );
}

function getActivePromo(item) {
    const promos = getProductPromos(item);

    if (promos.length === 0) return null;

    const priorityPromo = promos.find(
        promo => promo?.group_product_promo?.is_priority === true
    );

    if (isPromoActive(priorityPromo)) {
        return priorityPromo;
    }

    return promos
        .filter(promo => isPromoActive(promo))
        .sort((a, b) => {
            const dateA = new Date(a?.group_product_promo?.product_promo_dates?.[0]?.date_start || 0);
            const dateB = new Date(b?.group_product_promo?.product_promo_dates?.[0]?.date_start || 0);
            return dateA - dateB;
        })[0] || null;
}

function getActivePrice(item) {
    const prices = getProductPrices(item);

    if (prices.length === 0) return null;

    return prices
        .filter(price => isPriceActive(price))
        .sort((a, b) => {
            const dateA = new Date(a?.group_product_price?.product_price_dates?.[0]?.date_start || 0);
            const dateB = new Date(b?.group_product_price?.product_price_dates?.[0]?.date_start || 0);
            return dateA - dateB;
        })[0] || null;
}

export function posPriceDiscount(item, is_ecommerce = null) {
    if (!item) return currencyFormat(0);

    if (item.is_min_purchase === true) {
        return currencyFormat(item.price || 0);
    }

    if (item.is_clearance === true) {
        return currencyFormat(getClearancePrice(item));
    }

    const activePromo = getActivePromo(item);
    if (activePromo) {
        return currencyFormat(activePromo.price_sale);
    }

    const prices = getProductPrices(item);
    if (prices.length > 0) {
        if (is_ecommerce === true) {
            return currencyFormat(prices[0].price_ecommerce);
        }

        const validPrice = getActivePrice(item);
        return currencyFormat(validPrice ? validPrice.price_after_discount : prices[0].price_after_discount);
    }

    const categoryPrices = getProductCategoryPrices(item);
    if (categoryPrices.length > 0) {
        const dis = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_DISCOUNT_TYPE');
        const pric = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_PRICE_TYPE');

        if (pric) {
            if (pric.discount && dis?.discount) {
                const priceAfterDiscount = calculateDiscountedPrice(
                    Number(pric.discount.price),
                    Number(dis.discount.discount_percent)
                );
                return currencyFormat(priceAfterDiscount);
            }

            return currencyFormat(pric.price);
        }
    }

    if (item.discount) {
        if (Number(item.discount.price_after_discount) === 0) {
            return currencyFormat(item.price || 0);
        }

        return currencyFormat(item.discount.price_after_discount);
    }

    return currencyFormat(item.price || 0);
}

export function posPriceBeforeDiscount(item) {
    if (!item) return currencyFormat(0);

    if (item.is_min_purchase === true) {
        return currencyFormat(item.price || 0);
    }

    if (item.is_clearance === true) {
        return currencyFormat(
            item.price_normal ||
            item.product_price ||
            item.price ||
            getClearancePrice(item)
        );
    }

    const activePromo = getActivePromo(item);
    if (activePromo) {
        return currencyFormat(activePromo.price_normal);
    }

    const prices = getProductPrices(item);
    if (prices.length > 0) {
        return currencyFormat(prices[0].price_normal);
    }

    const categoryPrices = getProductCategoryPrices(item);
    if (categoryPrices.length > 0) {
        const pric = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_PRICE_TYPE');

        if (pric?.discount) {
            return currencyFormat(pric.discount.price);
        }

        if (pric) {
            return currencyFormat(pric.price);
        }
    }

    if (item.discount) {
        return currencyFormat(item.discount.price);
    }

    return currencyFormat(item.price || 0);
}

export function posPriceBeforeDiscountNum(item) {
    if (!item) return 0;

    if (item.is_min_purchase === true) {
        return Number(item.price || 0);
    }

    if (item.is_clearance === true) {
        return Number(
            item.price_normal ||
            item.product_price ||
            item.price ||
            getClearancePrice(item)
        );
    }

    const activePromo = getActivePromo(item);
    if (activePromo) {
        return Number(activePromo.price_normal || 0);
    }

    const prices = getProductPrices(item);
    if (prices.length > 0) {
        return Number(prices[0].price_normal || 0);
    }

    const categoryPrices = getProductCategoryPrices(item);
    if (categoryPrices.length > 0) {
        const pric = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_PRICE_TYPE');

        if (pric?.discount) {
            return Number(pric.discount.price || 0);
        }

        if (pric) {
            return Number(pric.price || 0);
        }
    }

    if (item.discount) {
        return Number(item.discount.price || 0);
    }

    return Number(item.price || 0);
}

export function posPriceNum(item, is_ecommerce = null) {
    if (!item) return 0;

    if (item.is_min_purchase === true) {
        return Number(item.price || 0);
    }

    if (item.is_clearance === true) {
        return getClearancePrice(item);
    }

    const activePromo = getActivePromo(item);
    if (activePromo) {
        return Number(activePromo.price_sale || 0);
    }

    const prices = getProductPrices(item);
    if (prices.length > 0) {
        if (is_ecommerce === true) {
            return Number(prices[0].price_ecommerce || 0);
        }

        const validPrice = getActivePrice(item);
        return Number(validPrice ? validPrice.price_after_discount : prices[0].price_after_discount);
    }

    const categoryPrices = getProductCategoryPrices(item);
    if (categoryPrices.length > 0) {
        const dis = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_DISCOUNT_TYPE');
        const pric = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_PRICE_TYPE');

        if (pric?.discount && dis?.discount) {
            const priceBefore = Number(pric.discount.price) * (Number(dis.discount.discount_percent) / 100);
            return Number(pric.discount.price) - priceBefore;
        }

        if (pric) {
            return Number(pric.price || 0);
        }
    }

    if (item.discount) {
        if (Number(item.discount.price_after_discount) === 0) {
            return Number(item.price || 0);
        }

        return Number(item.discount.price_after_discount || 0);
    }

    return Number(item.price || 0);
}

export function posPriceDiscountPercent(item, is_ecommerce = null) {
    if (!item || item.is_min_purchase === true || item.is_clearance === true) return null;

    const activePromo = getActivePromo(item);
    if (activePromo) {
        if (activePromo.discount_percent_2 !== null && Number(activePromo.discount_percent_2) !== 0) {
            return `${activePromo.discount_percent}% + ${activePromo.discount_percent_2}%`;
        }

        return `${activePromo.discount_percent}%`;
    }

    const prices = getProductPrices(item);
    if (prices.length > 0) {
        if (is_ecommerce === true) {
            return `${Number(prices[0].discount_percent_ecommerce || 0)}%`;
        }

        const validPrice = getActivePrice(item);
        return `${Number(validPrice ? validPrice.discount_percent : prices[0].discount_percent || 0)}%`;
    }

    const categoryPrices = getProductCategoryPrices(item);
    if (categoryPrices.length > 0) {
        const disc = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_DISCOUNT_TYPE');

        if (disc?.discount) {
            const discountPercent = Number(disc.discount.discount_percent || 0);
            const discountPercent2 = Number(disc.discount.discount_percent_2 || 0);

            if (discountPercent2 > 0) {
                return `${discountPercent}%+${discountPercent2}%`;
            }

            return `${discountPercent}%`;
        }
    }

    if (item.discount) {
        const discountPercent = Number(item.discount.discount_percent || 0);
        const discountPercent2 = Number(item.discount.discount_percent_2 || 0);

        if (discountPercent2 > 0) {
            return `${discountPercent}% + ${discountPercent2}%`;
        }

        return `${discountPercent}%`;
    }

    return null;
}

export function posPriceDiscountPercentNum(item, num, is_ecommerce = null) {
    if (!item || item.is_min_purchase === true || item.is_clearance === true) return 0;

    const activePromo = getActivePromo(item);
    if (activePromo) {
        return num === 1
            ? Number(activePromo.discount_percent || 0)
            : Number(activePromo.discount_percent_2 || 0);
    }

    const prices = getProductPrices(item);
    if (prices.length > 0) {
        if (is_ecommerce === true) {
            return Number(prices[0].discount_percent_ecommerce || 0);
        }

        const validPrice = getActivePrice(item);
        return Number(validPrice ? validPrice.discount_percent : prices[0].discount_percent || 0);
    }

    const categoryPrices = getProductCategoryPrices(item);
    if (categoryPrices.length > 0) {
        const disc = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_DISCOUNT_TYPE');

        if (disc?.discount) {
            return num === 1
                ? Number(disc.discount.discount_percent || 0)
                : Number(disc.discount.discount_percent_2 || 0);
        }
    }

    if (item.discount) {
        return num === 1
            ? Number(item.discount.discount_percent || 0)
            : Number(item.discount.discount_percent_2 || 0);
    }

    return 0;
}

export function posPriceDiscountPrice(item, is_ecommerce = null) {
    if (!item || item.is_min_purchase === true) return 0;

    if (item.is_clearance === true) {
        const normal = Number(posPriceBeforeDiscountNum(item));
        const clearance = Number(getClearancePrice(item));

        if (normal > clearance) {
            return normal - clearance;
        }

        return 0;
    }

    const activePromo = getActivePromo(item);
    if (activePromo) {
        const sale = Number(activePromo.price_sale || 0);
        const normal = Number(activePromo.price_normal || 0);
        return Number(normal - sale);
    }

    const prices = getProductPrices(item);
    if (prices.length > 0) {
        if (is_ecommerce === true) {
            return Number(prices[0].price_normal || 0) - Number(prices[0].price_ecommerce || 0);
        }

        const validPrice = getActivePrice(item);

        if (validPrice) {
            return Number(validPrice.price_normal || 0) - Number(validPrice.price_after_discount || 0);
        }

        return Number(prices[0].price_normal || 0) - Number(prices[0].price_after_discount || 0);
    }

    const categoryPrices = getProductCategoryPrices(item);
    if (categoryPrices.length > 0) {
        const dis = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_DISCOUNT_TYPE');
        const pric = categoryPrices.find(o => o.salesAdjustmentType === 'ITEM_PRICE_TYPE');

        if (pric?.discount && dis?.discount) {
            return Number(pric.discount.price || 0) * (Number(dis.discount.discount_percent || 0) / 100);
        }
    }

    if (item.discount) {
        return Number(item.discount.discount_price || 0);
    }

    return 0;
}